/**
 * Event Team Management System — types.
 *
 * Mirrors `docs/EVENT_TEAM_FRONTEND_GUIDE.md` §6 (verbatim where given) plus the
 * request/response/param shapes implied by the endpoint tables in §1 and the
 * API reference (`docs/EVENT_TEAM_API_DOCS.md`).
 *
 * Conventions (guide §0):
 * - Timestamps are unix **milliseconds**.
 * - Booleans from SQLite surface as `0 | 1`.
 * - Pagination is `?page=&limit=` (1-based); filterable lists return `{ results, total }`.
 */

import type { CalendarEvent } from './calendar';

// ── Core enums / constants ──────────────────────────────────────────────────

export type EventItemStatus =
  | 'idea'
  | 'approved'
  | 'scheduled'
  | 'announced'
  | 'in_progress'
  | 'completed'
  | 'on_hold'
  | 'cancelled';

export type EventProjectStatus =
  | 'planning'
  | 'in_progress'
  | 'review'
  | 'completed'
  | 'on_hold'
  | 'cancelled';

export type EventItemType = 'one_off' | 'recurring' | 'multi_stage';

export type EventMemberRole = 'organizer' | 'host' | 'lead' | 'manager' | 'admin';
export type EventMemberStatus = 'active' | 'inactive' | 'on_leave';

export type EventAssignmentRole = 'host' | 'organizer' | 'helper' | 'reviewer';
export type EventAssignmentStatus = 'active' | 'completed' | 'removed';

export type EventParticipantRole = 'participant' | 'winner' | 'runner_up' | 'helper';

export type EventTaskStatus = 'todo' | 'in_progress' | 'done' | 'skipped';

export type EventRetroOutcome = 'success' | 'partial_success' | 'needs_improvement' | 'failure';

export type EventWebLinkKind = 'announcement' | 'discord' | 'forum' | 'video' | 'doc' | 'other';

export type EventEntityLinkType =
  | 'related'
  | 'depends_on'
  | 'blocks'
  | 'follow_up_of'
  | 'part_of_series';

export type EventPointsEntryType = 'award' | 'deduction' | 'adjustment' | 'prefix_redemption';

export type EventCheckMatchMode = 'prefix' | 'exact' | 'regex';
export type EventCheckStatus = 'draft' | 'ready' | 'completed';
export type EventCheckResultStatus = 'clear' | 'flagged';
export type EventCheckTeamVerdict = 'clear' | 'flagged' | 'unchecked';

export const EVENT_ITEM_STATUSES: EventItemStatus[] = [
  'idea',
  'approved',
  'scheduled',
  'announced',
  'in_progress',
  'completed',
  'on_hold',
  'cancelled',
];

export const EVENT_PROJECT_STATUSES: EventProjectStatus[] = [
  'planning',
  'in_progress',
  'review',
  'completed',
  'on_hold',
  'cancelled',
];

export const EVENT_ITEM_TYPES: EventItemType[] = ['one_off', 'recurring', 'multi_stage'];

export const EVENT_MEMBER_ROLES: EventMemberRole[] = [
  'organizer',
  'host',
  'lead',
  'manager',
  'admin',
];
export const EVENT_MEMBER_STATUSES: EventMemberStatus[] = ['active', 'inactive', 'on_leave'];

export const EVENT_ASSIGNMENT_ROLES: EventAssignmentRole[] = [
  'host',
  'organizer',
  'helper',
  'reviewer',
];

export const EVENT_PARTICIPANT_ROLES: EventParticipantRole[] = [
  'participant',
  'winner',
  'runner_up',
  'helper',
];

export const EVENT_TASK_STATUSES: EventTaskStatus[] = ['todo', 'in_progress', 'done', 'skipped'];

export const EVENT_RETRO_OUTCOMES: EventRetroOutcome[] = [
  'success',
  'partial_success',
  'needs_improvement',
  'failure',
];

export const EVENT_WEB_LINK_KINDS: EventWebLinkKind[] = [
  'announcement',
  'discord',
  'forum',
  'video',
  'doc',
  'other',
];

export const EVENT_ENTITY_LINK_TYPES: EventEntityLinkType[] = [
  'related',
  'depends_on',
  'blocks',
  'follow_up_of',
  'part_of_series',
];

/** `prefix_redemption` entries are created automatically by spend-based grants — never post manually (guide §7.5). */
export const EVENT_POINTS_MANUAL_ENTRY_TYPES: EventPointsEntryType[] = [
  'award',
  'deduction',
  'adjustment',
];

export const EVENT_CHECK_MATCH_MODES: EventCheckMatchMode[] = ['prefix', 'exact', 'regex'];
export const EVENT_CHECK_STATUSES: EventCheckStatus[] = ['draft', 'ready', 'completed'];

/**
 * Minecraft classic palette (guide §2). Colors may also arrive as `#RRGGBB`
 * literals — pass those through unchanged.
 */
export const MC_COLORS: Record<string, string> = {
  black: '#000000',
  dark_blue: '#0000AA',
  dark_green: '#00AA00',
  dark_aqua: '#00AAAA',
  dark_red: '#AA0000',
  dark_purple: '#AA00AA',
  gold: '#FFAA00',
  gray: '#AAAAAA',
  dark_gray: '#555555',
  blue: '#5555FF',
  green: '#55FF55',
  aqua: '#55FFFF',
  red: '#FF5555',
  light_purple: '#FF55FF',
  yellow: '#FFFF55',
  white: '#FFFFFF',
};

// ── Pagination ──────────────────────────────────────────────────────────────

export interface EventPaginatedResponse<T> {
  results: T[];
  total: number;
  page?: number;
  limit?: number;
  total_pages?: number;
}

// ── Core entities (guide §6) ────────────────────────────────────────────────

export interface EventItem {
  id: string;
  project_id: string | null;
  title: string;
  description: string;
  status: EventItemStatus;
  priority: number;
  category: string | null;
  event_type: EventItemType;
  scheduled_start: number | null;
  scheduled_end: number | null;
  due_date: number | null;
  location: string | null;
  capacity: number | null;
  created_at: number;
  created_by: string;
  updated_at: number | null;
  updated_by: string | null;
  started_at: number | null;
  completed_at: number | null;
  metadata: string | null;
  /** Present on event detail (and some list payloads) after quest SNBT support. */
  quest_files_count?: number;
}

export interface EventProject {
  id: string;
  title: string;
  description: string;
  status: EventProjectStatus;
  priority: number;
  category: string | null;
  due_date: number | null;
  created_at: number;
  created_by: string;
  updated_at: number | null;
  updated_by: string | null;
  completed_at: number | null;
  metadata: string | null;
}

export interface EventTeamMember {
  id: string;
  minecraft_uuid: string;
  display_name: string;
  role: EventMemberRole;
  status: EventMemberStatus;
  specialties: string | null;
  joined_at: number;
  added_by: string;
  updated_at: number | null;
  updated_by: string | null;
  notes: string | null;
}

export interface EventAssignment {
  id: string;
  member_id: string;
  target_id: string;
  target_type: 'event' | 'project';
  role: EventAssignmentRole;
  self_assigned: 0 | 1;
  assigned_at: number;
  assigned_by: string;
  unassigned_at: number | null;
  unassigned_by: string | null;
  status: EventAssignmentStatus;
}

/** Minimal roster identity used for avatar rendering on the detail screens (guide §1.4). */
export interface EventAssignmentMember {
  id: string;
  minecraft_uuid: string;
  display_name: string;
}

export interface EventParticipant {
  id: string;
  event_id: string;
  user_uuid: string;
  display_name: string | null;
  role: EventParticipantRole;
  notes: string | null;
  added_at: number;
  added_by: string;
}

export interface EventWatcher {
  event_id: string;
  /** Some deployments name this `user_uuid` — read defensively. */
  watcher_uuid?: string;
  user_uuid?: string;
  watcher_email: string | null;
  watched_at?: number;
}

export interface EventPointsEntry {
  id: string;
  member_uuid: string;
  delta: number;
  entry_type: EventPointsEntryType;
  reason: string;
  event_id: string | null;
  prefix_grant_id: string | null;
  awarded_by: string;
  created_at: number;
}

export interface EventPointsTotal {
  member_uuid: string;
  display_name: string | null;
  total: number;
  entries_count: number;
  last_entry_at: number | null;
}

export interface Prefix {
  id: number;
  name: string;
  /** SQLite datetime affinity — surfaces as a datetime string (or number). */
  created: string | number;
  priority: number;
  /** Minecraft JSON text component; `null` → fall back to `name`. */
  prefix_json: string | null;
  category: string;
}

export interface PrefixHolding extends Omit<Prefix, 'created'> {
  user_id: number;
  user_uuid: string;
  prefix_id: number;
}

export interface EventPrefixGrant {
  id: string;
  prefix_id: number;
  member_uuid: string;
  status: 'active' | 'revoked';
  points_spent: number | null;
  notes: string | null;
  granted_by: string;
  granted_at: number;
  revoked_by: string | null;
  revoked_at: number | null;
}

export interface EventPrefixEligibilityRow {
  member_uuid: string;
  display_name: string | null;
  total_points: number;
  prefix_id: number;
  prefix_name: string;
  points_threshold: number;
  already_holds: boolean;
}

/** Overlay keyed by the live catalog `prefix_id` (backend doc §3.2 table 7). */
export interface EventPrefixRequirement {
  prefix_id: number;
  points_threshold: number | null;
  points_cost: number | null;
  notes: string | null;
  created_at?: number;
  created_by?: string;
  updated_at?: number | null;
  updated_by?: string | null;
}

export interface EventWorkflow {
  id: string;
  name: string;
  description: string | null;
  category: string | null;
  is_active: 0 | 1;
  created_at: number;
  created_by: string;
  updated_at: number | null;
  updated_by: string | null;
}

export interface EventWorkflowStage {
  id: string;
  workflow_id: string;
  title: string;
  description: string | null;
  position: number;
  default_role: string | null;
  /** Relative to the event's `scheduled_start` (negative = days before). */
  offset_days: number | null;
}

export interface EventWorkflowWithStages extends EventWorkflow {
  stages: EventWorkflowStage[];
}

export interface EventTask {
  id: string;
  event_id: string;
  title: string;
  description: string | null;
  status: EventTaskStatus;
  assignee_uuid: string | null;
  due_date: number | null;
  position: number;
  source_workflow_id: string | null;
  source_stage_id: string | null;
  created_at: number;
  created_by: string;
  updated_at: number | null;
  updated_by: string | null;
  completed_at: number | null;
  completed_by: string | null;
}

/** `GET /events/tasks/upcoming` rows include the parent event title. */
export interface EventUpcomingTask extends EventTask {
  event_title?: string;
}

export interface EventRetrospective {
  id: string;
  event_id: string;
  outcome: EventRetroOutcome | null;
  attendance_count: number | null;
  what_worked: string | null;
  what_didnt: string | null;
  lessons: string | null;
  created_at: number;
  created_by: string;
  updated_at: number | null;
  updated_by: string | null;
}

export interface EventMedia {
  id: string;
  target_id: string | null;
  target_type: 'event' | 'project' | null;
  r2_key: string;
  filename: string;
  content_type: string;
  size: number;
  caption: string | null;
  uploader_id: string;
  source_comment_id: string | null;
  created_at: number;
}

export interface EventComment {
  id: string;
  target_id: string;
  target_type: 'event' | 'project';
  content: string;
  author_id: string;
  created_at: number;
  updated_at: number | null;
  updated_by: string | null;
  images?: EventMedia[];
}

export interface EventTag {
  id: string;
  name: string;
  /** #RRGGBB */
  color: string;
  description: string | null;
  created_at?: number;
  created_by?: string;
}

export interface EventWebLink {
  id: string;
  target_id: string;
  target_type: 'event' | 'project';
  url: string;
  label: string | null;
  kind: EventWebLinkKind;
  linked_by: string;
  linked_at: number;
  updated_at: number | null;
  updated_by: string | null;
}

export interface EventServerLink {
  id: string;
  target_id: string;
  target_type: 'event' | 'project';
  fqdn: string | null;
  cluster_id: string | null;
  server_name: string | null;
  notes: string | null;
  linked_by: string;
  linked_at: number;
  updated_at?: number | null;
  updated_by?: string | null;
}

export interface EventModpackLink {
  id: string;
  target_id: string;
  target_type: 'event' | 'project';
  modpack_name: string;
  modpack_version: string | null;
  notes: string | null;
  linked_by: string;
  linked_at: number;
  updated_at?: number | null;
  updated_by?: string | null;
}

export interface EventTicketLink {
  ticket_id: number;
  item_id: string;
  item_type?: string;
  linked_by?: string;
  linked_at?: number;
}

export interface EventEntityLink {
  source_id: string;
  source_type: 'event' | 'project';
  target_id: string;
  target_type: 'event' | 'project';
  link_type: EventEntityLinkType;
  linked_by: string;
  linked_at: number;
  /** Denormalised target/source titles when the backend provides them. */
  title?: string | null;
  status?: string | null;
}

export interface EventEntityLinksResponse {
  outgoing: EventEntityLink[];
  incoming: EventEntityLink[];
}

/** Reverse lookup row from `GET /events/calendar/event/{calendarEventId}`. */
export interface EventCalendarReverseLink {
  target_id: string;
  target_type: 'event' | 'project';
  calendar_event_id: string;
  linked_by?: string;
  linked_at?: number;
  title?: string | null;
}

// ── Clearance (guide §3) ────────────────────────────────────────────────────

export interface EventPlayerTeam {
  id: string;
  event_id: string | null;
  name: string;
  color: string | null;
  notes: string | null;
  created_at: number;
  created_by: string;
  updated_at: number | null;
  updated_by: string | null;
}

export interface EventPlayerTeamMember {
  team_id: string;
  user_uuid: string;
  display_name: string | null;
  added_at?: number;
  added_by?: string;
}

export interface EventPlayerTeamWithMembers extends EventPlayerTeam {
  members: EventPlayerTeamMember[];
}

export interface EventCommandCheck {
  id: string;
  event_id: string | null;
  title: string;
  /** JSON array string in responses — `JSON.parse` for display (guide §7.10). */
  commands: string;
  match_mode: EventCheckMatchMode;
  scope_fqdn: string | null;
  scope_cluster: string | null;
  time_start: number;
  time_end: number;
  status: EventCheckStatus;
  notes: string | null;
  created_at: number;
  created_by: string;
  updated_at: number | null;
  updated_by: string | null;
}

export interface EventCheckViolation {
  id: string;
  result_id: string;
  user_uuid: string;
  display_name: string | null;
  command: string;
  used_at: number | null;
  server: string | null;
  es_doc_ref: string | null;
  details: string | null;
}

/** One row per (check, team) per run. */
export interface EventCheckResult {
  id: string;
  check_id: string;
  team_id: string;
  run_id: string;
  status: EventCheckResultStatus;
  members_checked: number;
  checked_by: string;
  checked_at: number;
  team_name?: string | null;
  violations?: EventCheckViolation[];
  violations_count?: number;
}

export interface EventCheckSummaryTeam {
  team_id: string;
  team_name: string;
  status: EventCheckTeamVerdict;
  members_checked?: number;
  violations_count?: number;
  violations?: EventCheckViolation[];
  result_id?: string;
}

export interface EventCheckSummary {
  check_id?: string;
  run_id: string | null;
  teams: EventCheckSummaryTeam[];
}

/** Field name assumptions from the backend; remap when the real mapping differs (guide §3.3). */
export interface EsQueryFieldHints {
  player_uuid_field: string;
  command_field: string;
  timestamp_field: string;
  server_field: string;
  cluster_field: string;
}

export interface EsQueryTeamSpec {
  team_id: string;
  team_name: string;
  member_uuids: string[];
  /** Ready-to-run ES search body (`size: 100`, sorted by timestamp ascending). */
  query: Record<string, unknown>;
}

export interface EsQuerySpec {
  check_signature?: string;
  check_id: string;
  match_mode: EventCheckMatchMode;
  field_hints: EsQueryFieldHints;
  teams: EsQueryTeamSpec[];
}

export interface SubmitCheckViolation {
  user_uuid: string;
  display_name?: string | null;
  command: string;
  used_at?: number | null;
  server?: string | null;
  es_doc_ref?: string | null;
  details?: string | null;
}

export interface SubmitCheckTeamResult {
  checked_uuids?: string[];
  team_id: string;
  status: EventCheckResultStatus;
  members_checked: number;
  violations?: SubmitCheckViolation[];
}

export interface SubmitCheckResultsResponse {
  run_id: string;
  results: EventCheckResult[];
}

export interface EventRewardPrefix {
  event_id: string; prefix_id: number; name: string; prefix_json: string | null; category: string; updated_at: number;
}
export interface CheckRewardPreview {
  event_id: string; check_id: string; run_id: string; checked_at: number;
  prefix: EventRewardPrefix;
  players: { uuid: string; status: 'eligible' | 'flagged' | 'already_held' | 'already_rewarded' | 'player_missing' }[];
}

// ── Kanban integration (guide §1.5) ─────────────────────────────────────────

export interface EventKanbanLink {
  event_id: string;
  card_id: string;
  board_id: string;
  sync_status: 0 | 1;
  sync_priority: 0 | 1;
  /** Event-specific dimension — incidents don't have this. */
  sync_due_date: 0 | 1;
  sync_assignees: 0 | 1;
  sync_comments: 0 | 1;
  propagate_watchers: 0 | 1;
  notes: string | null;
  linked_by: string;
  linked_at: number;
}

export type EventKanbanListRole =
  | 'backlog'
  | 'commitment_point'
  | 'in_progress'
  | 'delivery_point'
  | 'done'
  | 'none';

/** Denormalised row from `GET /events/items/{id}/workspaces`. */
export interface EventWorkspaceSummary {
  link: EventKanbanLink;
  board: {
    id: string;
    team: string;
    title: string;
    is_archived: number;
  };
  card: {
    id: string;
    title: string;
    list_id: string;
    list_title: string;
    list_role: EventKanbanListRole;
    is_archived: number;
    priority: number | null;
    due_date?: number | null;
    assignees_count: number;
  };
}

export interface UpdateEventKanbanLinkRequest {
  sync_status?: boolean;
  sync_priority?: boolean;
  sync_due_date?: boolean;
  sync_assignees?: boolean;
  sync_comments?: boolean;
  propagate_watchers?: boolean;
  notes?: string | null;
}

export interface CreateEventKanbanLinkRequest extends UpdateEventKanbanLinkRequest {
  card_id: string;
}

export interface PromoteEventToCardRequest {
  team: string;
  board_id?: string;
  list_id?: string;
  swimlane_id?: string | null;
  card_type?: string;
  copy_assignees?: boolean;
  notes?: string | null;
  sync_status?: boolean;
  sync_priority?: boolean;
  sync_due_date?: boolean;
  sync_assignees?: boolean;
  sync_comments?: boolean;
  propagate_watchers?: boolean;
}

export interface AdoptCardAsEventRequest {
  title?: string;
  description?: string;
  status?: EventItemStatus;
  priority?: number;
  project_id?: string | null;
  copy_assignees?: boolean;
  notes?: string | null;
  sync_status?: boolean;
  sync_priority?: boolean;
  sync_due_date?: boolean;
  sync_assignees?: boolean;
  sync_comments?: boolean;
  propagate_watchers?: boolean;
}

/** Same as `EventItem` plus count columns from `GET /events-with-kanban-summary` (top-level path!). */
export interface EventWithKanbanSummary extends EventItem {
  kanban_links_count: number;
  kanban_open_cards_count: number;
  kanban_done_cards_count: number;
  assignees_count: number;
  watchers_count: number;
}

export type EventTimelineSource =
  | 'event_comment'
  | 'event_activity'
  | 'kanban_activity'
  | 'kanban_comment'
  | 'kanban_card_history';

export interface EventTimelineEntry {
  source: EventTimelineSource;
  id: string;
  at: number;
  actor_id: string;
  summary: string;
  payload: unknown;
}

// ── Stats & activity ────────────────────────────────────────────────────────

export interface EventTeamStats {
  projects_by_status: Record<string, number>;
  events_by_status: Record<string, number>;
  total_projects: number;
  total_events: number;
  team_members_active: number;
  upcoming_events_7d: number;
  overdue_tasks: number;
  total_media: number;
  points_awarded_total: number;
  points_leaderboard_top: EventPointsTotal[];
  checks_flagged_teams: number;
}

export type EventActivityTargetType =
  | 'event'
  | 'project'
  | 'team_member'
  | 'workflow'
  | 'prefix'
  | 'check'
  | 'team';

export interface EventActivityLog {
  id: string;
  target_id: string;
  target_type: EventActivityTargetType;
  action: string;
  description: string;
  old_value: string | null;
  new_value: string | null;
  actor_id: string;
  created_at: number;
}

/** `GET /events/points/stats` — shape not fully documented; render defensively. */
export type EventPointsStats = Record<string, unknown>;

// ── Detail (with-relations) shapes ──────────────────────────────────────────

export interface EventItemFull extends EventItem {
  project: EventProject | null;
  assignments: EventAssignment[];
  assignment_members: EventAssignmentMember[];
  tags: EventTag[];
  tasks: EventTask[];
  participants: EventParticipant[];
  retrospective: EventRetrospective | null;
  web_links: EventWebLink[];
  comments_count: number;
  media_count: number;
  watchers_count: number;
  kanban_links_count: number;
}

export interface EventProjectFull extends EventProject {
  events: EventItem[];
  assignments: EventAssignment[];
  assignment_members?: EventAssignmentMember[];
  tags: EventTag[];
  web_links: EventWebLink[];
  server_links: EventServerLink[];
  modpack_links: EventModpackLink[];
  comments_count?: number;
  media_count?: number;
  events_count?: number;
}

/** Past-events archive rows carry the retrospective inline (guide §1.10). */
export interface EventPastItem extends EventItem {
  retrospective: EventRetrospective | null;
}

// ── Member drill-down / points / prefixes responses ─────────────────────────

export interface EventMemberPointsResponse {
  member_uuid?: string;
  total: number;
  entries: EventPointsEntry[];
}

export interface EventMemberPrefixesResponse {
  member_uuid?: string;
  holdings: PrefixHolding[];
  grants: EventPrefixGrant[];
  points_total?: number;
}

export interface EventPointsMemberSummary {
  member_uuid: string;
  total: number;
  entries: EventPointsEntry[];
}

/** `GET /events/players/{uuid}/prefixes` — works for ANY player, not just roster members. */
export interface PlayerPrefixesResponse {
  user_uuid: string;
  user_id: number | null;
  holdings: PrefixHolding[];
  event_grants: EventPrefixGrant[];
  event_points_total: number;
}

export interface GrantPrefixResponse {
  grant: EventPrefixGrant;
  /** True when the player already held the prefix — treat as success (history recorded). */
  already_held: boolean;
  points_entry: EventPointsEntry | null;
}

export interface PrefixWithRequirement extends Prefix {
  requirement: EventPrefixRequirement | null;
}

// ── Workflow application / scheduling responses ─────────────────────────────

export interface ApplyWorkflowResponse {
  event_id: string;
  workflow_id: string;
  tasks_created: number;
  tasks: EventTask[];
}

export interface ScheduleEventResponse {
  calendar_event: CalendarEvent;
  event: EventItem;
}

// ── Request bodies ──────────────────────────────────────────────────────────

export interface CreateEventItemRequest {
  title: string;
  description: string;
  status?: EventItemStatus;
  priority?: number;
  category?: string | null;
  event_type?: EventItemType;
  project_id?: string | null;
  scheduled_start?: number | null;
  scheduled_end?: number | null;
  due_date?: number | null;
  location?: string | null;
  capacity?: number | null;
  metadata?: string | null;
  tag_ids?: string[];
}

export interface UpdateEventItemRequest {
  title?: string;
  description?: string;
  status?: EventItemStatus;
  priority?: number;
  category?: string | null;
  event_type?: EventItemType;
  scheduled_start?: number | null;
  scheduled_end?: number | null;
  due_date?: number | null;
  location?: string | null;
  capacity?: number | null;
  metadata?: string | null;
}

export interface CreateEventProjectRequest {
  title: string;
  description: string;
  status?: EventProjectStatus;
  priority?: number;
  category?: string | null;
  due_date?: number | null;
  metadata?: string | null;
  tag_ids?: string[];
}

export interface UpdateEventProjectRequest {
  title?: string;
  description?: string;
  status?: EventProjectStatus;
  priority?: number;
  category?: string | null;
  due_date?: number | null;
  metadata?: string | null;
}

export interface CreateEventMemberRequest {
  minecraft_uuid: string;
  display_name: string;
  role?: EventMemberRole;
  status?: EventMemberStatus;
  specialties?: string | null;
  notes?: string | null;
}

export interface UpdateEventMemberRequest {
  display_name?: string;
  role?: EventMemberRole;
  status?: EventMemberStatus;
  specialties?: string | null;
  notes?: string | null;
}

export interface CreateEventTaskRequest {
  title: string;
  description?: string | null;
  status?: EventTaskStatus;
  assignee_uuid?: string | null;
  due_date?: number | null;
  position?: number;
}

export interface UpdateEventTaskRequest {
  title?: string;
  description?: string | null;
  status?: EventTaskStatus;
  assignee_uuid?: string | null;
  due_date?: number | null;
  position?: number;
}

export interface CreateEventWorkflowRequest {
  name: string;
  description?: string | null;
  category?: string | null;
  stages?: {
    title: string;
    description?: string | null;
    default_role?: string | null;
    offset_days?: number | null;
  }[];
}

export interface UpdateEventWorkflowRequest {
  name?: string;
  description?: string | null;
  category?: string | null;
  is_active?: boolean;
}

export interface CreateEventWorkflowStageRequest {
  title: string;
  description?: string | null;
  default_role?: string | null;
  offset_days?: number | null;
  position?: number;
}

export interface UpdateEventWorkflowStageRequest {
  title?: string;
  description?: string | null;
  default_role?: string | null;
  offset_days?: number | null;
  /** Fractional positions supported for drag-reorder (guide §1.9). */
  position?: number;
}

export interface UpsertEventRetrospectiveRequest {
  outcome?: EventRetroOutcome | null;
  attendance_count?: number | null;
  what_worked?: string | null;
  what_didnt?: string | null;
  lessons?: string | null;
}

export interface AwardPointsRequest {
  member_uuid: string;
  /** Signed, non-zero. Negative deltas default entry_type to `deduction`. */
  delta: number;
  reason: string;
  entry_type?: EventPointsEntryType;
  event_id?: string | null;
}

export interface GrantPrefixRequest {
  member_uuid: string;
  notes?: string | null;
  spend_points?: boolean;
}

export interface UpsertPrefixRequirementRequest {
  points_threshold?: number | null;
  points_cost?: number | null;
  notes?: string | null;
}

export interface CreateEventWebLinkRequest {
  url: string;
  label?: string | null;
  kind?: EventWebLinkKind;
}

export interface UpdateEventWebLinkRequest {
  url?: string;
  label?: string | null;
  kind?: EventWebLinkKind;
}

export interface CreateEventServerLinkRequest {
  fqdn?: string | null;
  cluster_id?: string | null;
  server_name?: string | null;
  notes?: string | null;
}

export interface UpdateEventServerLinkRequest extends CreateEventServerLinkRequest {}

export interface CreateEventModpackLinkRequest {
  modpack_name: string;
  modpack_version?: string | null;
  notes?: string | null;
}

export interface UpdateEventModpackLinkRequest {
  modpack_name?: string;
  modpack_version?: string | null;
  notes?: string | null;
}

export interface CreateEventParticipantRequest {
  user_uuid: string;
  display_name?: string | null;
  role?: EventParticipantRole;
  notes?: string | null;
}

export interface CreateEventEntityLinkRequest {
  target_id: string;
  target_type: 'event' | 'project';
  link_type?: EventEntityLinkType;
}

export interface ScheduleEventRequest {
  start_time: number;
  end_time?: number | null;
  calendar_title?: string;
  calendar_description?: string;
  tag_ids?: string[];
  publish?: boolean;
}

export interface CreateEventTagRequest {
  name: string;
  /** #RRGGBB */
  color: string;
  description?: string | null;
}

export interface UpdateEventTagRequest {
  name?: string;
  color?: string;
  description?: string | null;
}

export interface CreatePlayerTeamRequest {
  name: string;
  event_id?: string | null;
  color?: string | null;
  notes?: string | null;
  members?: { user_uuid: string; display_name?: string | null }[];
}

export interface UpdatePlayerTeamRequest {
  name?: string;
  event_id?: string | null;
  color?: string | null;
  notes?: string | null;
}

export interface CreateCommandCheckRequest {
  title: string;
  /** Submitted as a real array (responses return a JSON string — guide §7.10). */
  commands: string[];
  match_mode?: EventCheckMatchMode;
  scope_fqdn?: string | null;
  scope_cluster?: string | null;
  time_start: number;
  time_end: number;
  event_id?: string | null;
  status?: EventCheckStatus;
  notes?: string | null;
}

export interface UpdateCommandCheckRequest {
  title?: string;
  commands?: string[];
  match_mode?: EventCheckMatchMode;
  scope_fqdn?: string | null;
  scope_cluster?: string | null;
  time_start?: number;
  time_end?: number;
  event_id?: string | null;
  status?: EventCheckStatus;
  notes?: string | null;
}

// ── List params ─────────────────────────────────────────────────────────────

export type EventItemSortBy =
  | 'created_at'
  | 'priority'
  | 'status'
  | 'due_date'
  | 'scheduled_start'
  | 'updated_at'
  | 'title';

export interface EventItemListParams {
  status?: string;
  category?: string;
  event_type?: string;
  project_id?: string;
  /** Roster member id (not uuid) — guide §1.3. */
  assignee?: string;
  tag_id?: string;
  search?: string;
  priority_min?: number | string;
  priority_max?: number | string;
  scheduled_after?: number | string;
  scheduled_before?: number | string;
  due_after?: number | string;
  due_before?: number | string;
  sort_by?: EventItemSortBy;
  sort_order?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface EventProjectListParams {
  status?: string;
  category?: string;
  assignee?: string;
  tag_id?: string;
  search?: string;
  priority_min?: number | string;
  priority_max?: number | string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface EventPointsLedgerParams {
  member_uuid?: string;
  event_id?: string;
  entry_type?: EventPointsEntryType | '';
  page?: number;
  limit?: number;
}
